<template>
    <div v-if="showProducts" class="panel">
        <Notification
            v-if="page === DevPanelPage.Booking"
            title="Date sorting not working yet"
            variant="warning"
            :tier="1" />

        <h3>
            Products <span v-if="products.length">({{ products.length }})</span>
        </h3>

        <div class="panel-actions">
            <div>Add new product</div>
            <div class="panel-actions-list">
                <ButtonPrimary
                    v-for="(a, index) in actions"
                    :onClick="() => onAdd(a)"
                    :key="index"
                    :icon="getProductIcon(a)"
                    size="s">
                    {{ a }}
                </ButtonPrimary>
            </div>
        </div>

        <div v-if="products.length" class="panel-products">
            <div
                v-for="(p, index) in products"
                class="panel-product"
                :key="p.id"
                :style="{
                    borderLeft: `10px ${generateColorFromId(p.trip.id)} solid`,
                }">
                <div class="panel-product-inner">
                    <div class="panel-product-header">
                        <Icon :type="getProductIcon(p.type)" />

                        <template v-if="showProductsOptions">
                            <div v-if="p.type === 'Flight'" class="panel-product-options">
                                <Checkbox
                                    density="compact"
                                    v-model="p.isRoundTrip"
                                    @update:modelValue="() => setProductsTrips(products)">
                                    Round Trip
                                </Checkbox>

                                <Select
                                    density="compact"
                                    v-model="p.platingCarrier"
                                    :items="carriers"
                                    label="Carrier">
                                    Plating carrier
                                </Select>
                            </div>

                            <div v-if="p.type === 'Ferry'" class="panel-product-options">
                                <Checkbox
                                    density="compact"
                                    v-model="p.isRoundTrip"
                                    @update:modelValue="() => setProductsTrips(products)">
                                    Round Trip
                                </Checkbox>
                            </div>
                        </template>
                    </div>

                    <div class="panel-product-actions">
                        <ButtonIcon
                            :onClick="() => onMoveDirection(p.id, 'Up')"
                            :disabled="index === 0 || page === DevPanelPage.Booking"
                            icon="Arrow"
                            :iconRotate="90"
                            ariaLabel="increase"
                            size="s" />

                        <ButtonIcon
                            :onClick="() => onMoveDirection(p.id, 'Down')"
                            :disabled="
                                index === products.length - 1 || page === DevPanelPage.Booking
                            "
                            icon="Arrow"
                            :iconRotate="270"
                            ariaLabel="decrease"
                            size="s" />

                        <ButtonIcon
                            icon="Bin"
                            :onClick="() => onDelete(p.id)"
                            ariaLabel="remove"
                            size="s" />
                    </div>
                </div>
            </div>
        </div>
        <Notification v-else title="No products added" variant="negative" :tier="1" />
    </div>
</template>

<script lang="ts">
    import { computed, watch, defineComponent, PropType } from 'vue';
    import { DevPanelPage, FakeDataProduct } from '../DevPanel.interfaces';
    import { isBookingsPage, isCheckoutPage } from '@/helpers/url';
    import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
    import { generateUUID } from '@shared/utils/uuid';
    import { getDateAddingDuration } from '@shared/helpers/datetime/datetime';
    import { getLocaleDayShortMonthYear } from '@shared/helpers/datetime/datetime.formatters';
    import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
    import { AvailableCompanies } from '@/api/Cart/cart.fake';
    import Checkbox from '@ui/components/FormV2/Fields/Checkbox/Checkbox.vue';
    import Select from '@ui/components/FormV2/Fields/Select/Select.vue';
    import ButtonIcon from '@ui/components/Button/ButtonIcon.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import Icon from '@ui/components/Icon/Icon.vue';

    interface Trip {
        id: string;
        products: FakeDataProduct[];
    }

    export default defineComponent({
        name: 'ProductsPanel',
        components: {
            ButtonIcon,
            ButtonPrimary,
            Checkbox,
            Select,
            Icon,
            Notification,
        },
        props: {
            products: {
                type: Object as PropType<FakeDataProduct[]>,
                required: true,
            },
            page: { type: String as PropType<DevPanelPage> },
        },
        emits: ['update:products'],
        setup(props, { emit }) {
            const showProducts = computed<boolean>(() => !isBookingsPage());
            const showProductsOptions = computed<boolean>(() => isCheckoutPage());

            const actions: string[] = ['Flight', 'Ferry', 'Hotel', 'Activity', 'Transfer'];

            const carriers = computed<SelectItem[]>(() => [
                { value: undefined, label: 'RANDOM CARRIER' },
                ...AvailableCompanies.map((x) => ({ value: x.code, label: x.name })),
            ]);

            function getProductIcon(type: string): IconType {
                switch (type) {
                    case 'Hotel':
                        return 'Bed';
                    case 'Ferry':
                        return 'Ferry';
                    case 'Activity':
                        return 'Ticket';
                    case 'Transfer':
                        return 'MiniBus';
                    default:
                        return 'Plane';
                }
            }

            function onAdd(type: string) {
                const updateProducts = [
                    ...props.products,
                    {
                        id: generateUUID(),
                        type,
                        days: 1,
                        isRoundTrip: false,
                    },
                ];

                emit('update:products', updateProducts);
            }

            function onDelete(id: string) {
                const updatedProducts = props.products.filter((product) => product.id !== id);

                emit('update:products', updatedProducts);
            }

            function onMoveDirection(id: string, direction: 'Up' | 'Down') {
                const index = props.products.findIndex((product) => product.id === id);

                if (index === -1) return;
                const updatedProducts = [...props.products];

                if (direction === 'Up' && index > 0) {
                    const temp = updatedProducts[index - 1];
                    updatedProducts[index - 1] = updatedProducts[index];
                    updatedProducts[index] = temp;
                } else if (direction === 'Down' && index < updatedProducts.length - 1) {
                    const temp = updatedProducts[index + 1];
                    updatedProducts[index + 1] = updatedProducts[index];
                    updatedProducts[index] = temp;
                }

                emit('update:products', updatedProducts);
            }

            function generateColorFromId(id: string) {
                // @ts-ignore
                const hash = id.toString(16);

                const r = parseInt(hash.slice(0, 2), 16) || 0;
                const g = parseInt(hash.slice(2, 4), 16) || 0;
                const b = parseInt(hash.slice(4, 6), 16) || 0;
                const color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

                return color;
            }

            function setProductsTrips(products: FakeDataProduct[]) {
                const trips: Trip[] = [];

                // Group products into trips
                products.forEach((product) => {
                    const trip = trips[trips.length - 1];

                    const hasRoundTrip =
                        (product.type === 'Flight' || product.type === 'Ferry') &&
                        product.isRoundTrip;

                    if (hasRoundTrip || !trip) {
                        const tripId = product.id;
                        product.trip = { id: tripId };
                        trips.push({ id: tripId, products: [product] });
                    } else {
                        product.trip = { id: trip.id };
                        trip.products.push(product);
                    }
                });
            }

            function setPropertyValue(id: string, propName: string, value: any) {
                const updatedProducts = props.products.map((p) => {
                    if (p.id === id) {
                        return { ...p, [propName]: value };
                    }

                    return p;
                });

                emit('update:products', updatedProducts);
            }

            function getDateFromDays(days: number) {
                return getLocaleDayShortMonthYear(
                    getDateAddingDuration(new Date(), { days })
                );
            }

            watch(
                () => props.products,
                (newProducts, oldProducts) => {
                    if (JSON.stringify(newProducts) !== JSON.stringify(oldProducts)) {
                        setProductsTrips(newProducts);
                    }
                },
                { immediate: true, deep: true }
            );

            return {
                showProducts,
                showProductsOptions,
                onAdd,
                onDelete,
                onMoveDirection,
                actions,
                carriers,
                setPropertyValue,
                generateColorFromId,
                getProductIcon,
                setProductsTrips,
                getDateFromDays,
                DevPanelPage,
            };
        },
    });
</script>

<style lang="scss">
    @forward './Panel.scss';
</style>
